import type { SpeakerCard, SpeakerFieldErrors } from './speakers.types';
import type { GetWebcastDescriptionQuery, SpeakerInput } from '../../../../../generated/graphql-manager';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { omitKey } from './speakers.utils';
import { validateSpeakerName, validateSpeakerPosition } from './speakers.validation';
import { useSpeakerImageUploader } from './use-speaker-image-uploader';

export const MAX_SPEAKERS = 6;

type ServerSpeaker = NonNullable<GetWebcastDescriptionQuery['webcast']>['speakers'][number];

type UseSpeakersOptions = {
  webcastId: string;
  serverSpeakers: readonly ServerSpeaker[] | undefined;
  disabled?: boolean;
};

export type UseSpeakers = {
  cards: SpeakerCard[];
  errors: Record<string, SpeakerFieldErrors>;
  /** Per-card flag: differs from the saved server state (a new card is always dirty). */
  dirtyByKey: Record<string, boolean>;
  /** Per-card flag: the card's picked image is uploading during a Save. */
  uploadingByKey: Record<string, boolean>;
  hasUnsavedChanges: boolean;
  canAddSpeaker: boolean;
  addSpeaker: () => void;
  changeName: (cardKey: string, value: string) => void;
  changePosition: (cardKey: string, value: string) => void;
  blurName: (cardKey: string) => void;
  blurPosition: (cardKey: string) => void;
  selectImage: (cardKey: string, file: File) => void;
  removeImage: (cardKey: string) => void;
  deleteCard: (cardKey: string) => void;
  /** Validate all cards (name required; position length). Sets field errors; returns true if valid. */
  validate: () => boolean;
  /** Upload any locally-picked images and return the full speakers list for `updateWebcast`. */
  buildSpeakerInputs: () => Promise<SpeakerInput[]>;
};

function toCard(speaker: ServerSpeaker): SpeakerCard {
  return {
    cardKey: speaker.id,
    id: speaker.id,
    name: speaker.name,
    position: speaker.position ?? '',
    picture: speaker.picture,
  };
}

export function useSpeakers({ webcastId, serverSpeakers, disabled }: UseSpeakersOptions): UseSpeakers {
  const { uploadImage } = useSpeakerImageUploader(webcastId);

  const [cards, setCards] = useState<SpeakerCard[]>([]);
  const [errors, setErrors] = useState<Record<string, SpeakerFieldErrors>>({});
  const [uploadingByKey, setUploadingByKey] = useState<Record<string, boolean>>({});

  // Always-current snapshot of `cards`, read inside event/async callbacks so we never act on a stale
  // closure and never put side effects (revoke) inside a setState updater.
  const cardsRef = useRef(cards);
  cardsRef.current = cards;

  const newKeyCounterRef = useRef(0);
  // All `blob:` preview URLs we created, so we can revoke them on reset/unmount.
  const previewUrlsRef = useRef<Set<string>>(new Set());

  const createPreview = useCallback((file: File) => {
    const url = URL.createObjectURL(file);
    previewUrlsRef.current.add(url);
    return url;
  }, []);

  const revokePreview = useCallback((url: string | undefined) => {
    if (!url) return;
    URL.revokeObjectURL(url);
    previewUrlsRef.current.delete(url);
  }, []);

  // Seed (and re-seed after a successful save) the working copy from the server. `serverSpeakers` is
  // referentially stable while editing, so local edits are never clobbered — it only changes on the
  // initial load and when the save updates the normalized cache.
  useEffect(() => {
    if (!serverSpeakers) return;
    previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    previewUrlsRef.current.clear();
    setCards(serverSpeakers.map(toCard));
    setErrors({});
  }, [serverSpeakers]);

  // Revoke any outstanding previews on unmount.
  useEffect(
    () => () => {
      previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      previewUrlsRef.current.clear();
    },
    []
  );

  const setFieldError = useCallback((cardKey: string, field: keyof SpeakerFieldErrors, message?: string) => {
    setErrors((prev) => ({ ...prev, [cardKey]: { ...prev[cardKey], [field]: message } }));
  }, []);

  const dirtyByKey = useMemo(() => {
    const serverById = new Map((serverSpeakers ?? []).map((speaker) => [speaker.id, speaker]));
    const map: Record<string, boolean> = {};

    cards.forEach((card) => {
      if (!card.id) {
        map[card.cardKey] = true;
        return;
      }
      const server = serverById.get(card.id);
      if (!server) {
        map[card.cardKey] = true;
        return;
      }
      const nameChanged = card.name.trim() !== server.name;
      const positionChanged = card.position.trim() !== (server.position ?? '');
      const imageChanged =
        Boolean(card.pendingFile) || (card.picture?.assetId ?? null) !== (server.picture?.assetId ?? null);
      map[card.cardKey] = nameChanged || positionChanged || imageChanged;
    });

    return map;
  }, [cards, serverSpeakers]);

  const hasUnsavedChanges = useMemo(() => {
    const cardIds = new Set(cards.filter((card) => card.id).map((card) => card.id));
    const deleted = (serverSpeakers ?? []).some((speaker) => !cardIds.has(speaker.id));
    return deleted || Object.values(dirtyByKey).some(Boolean);
  }, [cards, serverSpeakers, dirtyByKey]);

  const addSpeaker = useCallback(() => {
    if (disabled) return;
    setCards((prev) => {
      if (prev.length >= MAX_SPEAKERS) return prev;
      newKeyCounterRef.current += 1;
      return [...prev, { cardKey: `new-${newKeyCounterRef.current}`, name: '', position: '' }];
    });
  }, [disabled]);

  const changeName = useCallback(
    (cardKey: string, value: string) => {
      setCards((prev) => prev.map((card) => (card.cardKey === cardKey ? { ...card, name: value } : card)));
      setFieldError(cardKey, 'name', undefined);
    },
    [setFieldError]
  );

  const changePosition = useCallback(
    (cardKey: string, value: string) => {
      setCards((prev) => prev.map((card) => (card.cardKey === cardKey ? { ...card, position: value } : card)));
      setFieldError(cardKey, 'position', undefined);
    },
    [setFieldError]
  );

  const blurName = useCallback(
    (cardKey: string) => {
      const card = cardsRef.current.find((item) => item.cardKey === cardKey);
      if (card) setFieldError(cardKey, 'name', validateSpeakerName(card.name));
    },
    [setFieldError]
  );

  const blurPosition = useCallback(
    (cardKey: string) => {
      const card = cardsRef.current.find((item) => item.cardKey === cardKey);
      if (card) setFieldError(cardKey, 'position', validateSpeakerPosition(card.position));
    },
    [setFieldError]
  );

  const selectImage = useCallback(
    (cardKey: string, file: File) => {
      if (disabled) return;
      const card = cardsRef.current.find((item) => item.cardKey === cardKey);
      if (!card) return;

      revokePreview(card.previewUrl);
      const previewUrl = createPreview(file);
      setCards((prev) =>
        prev.map((item) => (item.cardKey === cardKey ? { ...item, pendingFile: file, previewUrl } : item))
      );
    },
    [createPreview, disabled, revokePreview]
  );

  const removeImage = useCallback(
    (cardKey: string) => {
      if (disabled) return;
      const card = cardsRef.current.find((item) => item.cardKey === cardKey);
      if (!card) return;

      revokePreview(card.previewUrl);
      setCards((prev) =>
        prev.map((item) =>
          item.cardKey === cardKey ? { ...item, pendingFile: undefined, previewUrl: undefined, picture: null } : item
        )
      );
    },
    [disabled, revokePreview]
  );

  const deleteCard = useCallback(
    (cardKey: string) => {
      if (disabled) return;
      const card = cardsRef.current.find((item) => item.cardKey === cardKey);
      revokePreview(card?.previewUrl);
      setCards((prev) => prev.filter((item) => item.cardKey !== cardKey));
      setErrors((prev) => omitKey(prev, cardKey));
    },
    [disabled, revokePreview]
  );

  // Validate all cards (only the name is required; position is length-checked) and surface field
  // errors. Returns true when every card is valid.
  const validate = useCallback((): boolean => {
    const nextErrors: Record<string, SpeakerFieldErrors> = {};
    let valid = true;
    cardsRef.current.forEach((card) => {
      const name = validateSpeakerName(card.name);
      const position = validateSpeakerPosition(card.position);
      if (name || position) {
        nextErrors[card.cardKey] = { name, position };
        valid = false;
      }
    });
    setErrors(nextErrors);
    return valid;
  }, []);

  // Upload any locally-picked images (deferred until Save), then build the full speakers list for
  // `updateWebcast` — replace semantics: omitted = removed, no id = added, id = updated. Rejects if an
  // image upload fails. The page sends this together with the descriptions in a single mutation.
  const buildSpeakerInputs = useCallback(async (): Promise<SpeakerInput[]> => {
    const current = cardsRef.current;

    const assetIdByKey: Record<string, string> = {};
    await Promise.all(
      current
        .filter((card) => card.pendingFile)
        .map(async (card) => {
          // Flag this card as uploading so its avatar shows a spinner, then clear it when done.
          setUploadingByKey((prev) => ({ ...prev, [card.cardKey]: true }));
          try {
            assetIdByKey[card.cardKey] = await uploadImage(card.pendingFile as File);
          } finally {
            setUploadingByKey((prev) => omitKey(prev, card.cardKey));
          }
        })
    );

    return current.map((card) => {
      const position = card.position.trim();
      const input: SpeakerInput = {
        name: card.name.trim(),
        position: position.length ? position : null,
        pictureAssetId: assetIdByKey[card.cardKey] ?? card.picture?.assetId ?? null,
      };
      if (card.id) input.id = card.id;
      return input;
    });
  }, [uploadImage]);

  const canAddSpeaker = !disabled && cards.length < MAX_SPEAKERS;

  return {
    cards,
    errors,
    dirtyByKey,
    uploadingByKey,
    hasUnsavedChanges,
    canAddSpeaker,
    addSpeaker,
    changeName,
    changePosition,
    blurName,
    blurPosition,
    selectImage,
    removeImage,
    deleteCard,
    validate,
    buildSpeakerInputs,
  };
}
